import React, { useState, useCallback, RefObject } from 'react';

interface UseLinkEditingProps {
  isEditMode: boolean;
  contentRef: RefObject<HTMLDivElement>;
  onContentChange: (content: string) => void;
}

export const useLinkEditing = ({ isEditMode, contentRef, onContentChange }: UseLinkEditingProps) => {
  const [editingAnchor, setEditingAnchor] = useState<HTMLAnchorElement | null>(null);

  const handleLinkClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isEditMode) return;

      const target = e.target as HTMLElement;

      // Check if the clicked element is an anchor or within an anchor
      let anchorElement: HTMLAnchorElement | null = null;
      if (target.tagName === 'A') {
        anchorElement = target as HTMLAnchorElement;
        console.log('A anchor', anchorElement);
      } else {
        anchorElement = target.closest('a') as HTMLAnchorElement;
        console.log('[a] anchor', anchorElement);
      }

      if (anchorElement) {
        // Prevent navigation when clicking on links in edit mode
        e.preventDefault();
        e.stopPropagation();
        setEditingAnchor(anchorElement);
      }
    },
    [isEditMode],
  );

  const handleLinkSave = useCallback(() => {
    if (editingAnchor && contentRef.current) {
      // The LinkEditPopover component handles setting attributes directly
      // We just need to update the content

      // Check if the link should be removed (empty href)
      const currentHref = editingAnchor.getAttribute('href');
      if (!currentHref || currentHref.trim() === '') {
        // If URL is empty, remove the link and convert back to plain text
        const linkText = editingAnchor.textContent || '';
        const textNode = document.createTextNode(linkText);
        editingAnchor.parentNode?.replaceChild(textNode, editingAnchor);
      }

      // Get the updated HTML content
      const updatedContent = contentRef.current.innerHTML;

      // Update the state with the new content
      onContentChange(updatedContent);
    }
    setEditingAnchor(null);
  }, [editingAnchor, contentRef, onContentChange]);

  const handleLinkEditClose = useCallback(() => {
    setEditingAnchor(null);
  }, []);

  return {
    editingAnchor,
    setEditingAnchor,
    handleLinkClick,
    handleLinkSave,
    handleLinkEditClose,
  };
};
